import { useState, useMemo, MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";
import {
  Box,
  Button,
  CenteredColBox,
  ErrorLabel,
  Form,
  GradientBorderedBox,
  Icon,
  Link,
} from "../../../common/components";
import { MaterialSymbolType } from "../../../common/components/Icon/IconConstant";
import { useForm } from "../../../common/hooks/useForm";
import { spacing, palette } from "../../../styles/theme";
import { ATTRACTION_PRIMARY_MARKETS } from "../../../constant/common.constant";
import { OnboardingTitle } from "../Component/OnboardingTitle";
import { Typography } from "@mui/material";

interface Props {
  onNextStep: (market: ATTRACTION_PRIMARY_MARKETS) => void;
  onPreviousStep: (e: MouseEvent<HTMLAnchorElement>) => void;
  selectedMarket?: ATTRACTION_PRIMARY_MARKETS;
}

const marketIcons: Record<
  ATTRACTION_PRIMARY_MARKETS,
  { base: MaterialSymbolType; active: MaterialSymbolType }
> = {
  [ATTRACTION_PRIMARY_MARKETS.PROMO]: { base: "sell", active: "loyalty" },
  [ATTRACTION_PRIMARY_MARKETS.SOUVENIR]: { base: "wb_sunny", active: "sunny" },
  [ATTRACTION_PRIMARY_MARKETS.BUSINESS]: { base: "work", active: "cases" },
  [ATTRACTION_PRIMARY_MARKETS.OTHER]: { base: "category", active: "interests" },
};

export const PrimaryMarket = ({
  onNextStep,
  onPreviousStep,
  selectedMarket,
}: Props) => {
  const { t } = useTranslation();
  const [hovering, setHovering] = useState<ATTRACTION_PRIMARY_MARKETS | null>(
    null
  );

  const validationSchema = useMemo(
    () =>
      object({
        market: string().required(t("general.errors.required.listItem")),
      }),
    [t]
  );

  const formik = useForm({
    initialValues: { market: selectedMarket ?? "" },
    onSubmit: (values) => {
      const validMarkets = Object.values(
        ATTRACTION_PRIMARY_MARKETS
      ) as string[];
      if (validMarkets.includes(values.market)) {
        onNextStep(values.market as ATTRACTION_PRIMARY_MARKETS);
      }
    },
    validationSchema,
  });

  return (
    <Form formik={formik}>
      <Box fullWidth mt={{ xs: spacing.xlMobile, lg: 0 }}>
        <OnboardingTitle title={t("attraction.onboard.primary_market.title")} />
        <CenteredColBox
          gap={{ xs: spacing.smallMobile, lg: spacing.smallDesktop }}
          fullWidth
        >
          {Object.values(ATTRACTION_PRIMARY_MARKETS).map((market) => {
            const isActive = formik.values.market === market;
            const isHovering = hovering === market;
            const icon =
              isActive || isHovering
                ? marketIcons[market].active
                : marketIcons[market].base;
            return (
              <GradientBorderedBox
                key={market}
                display="flex"
                alignItems="center"
                gap={{ xs: spacing.smallMobile, lg: spacing.smallDesktop }}
                showGradient={isActive}
                active={isActive}
                onClick={() => formik.setFieldValue("market", market)}
                onMouseEnter={() => setHovering(market)}
                onMouseLeave={() => setHovering(null)}
                fullWidth
                data-test={`onboard.primaryMarket.${market}`}
              >
                <Icon icon={icon} size={20} color={palette.darkGray} />
                <Typography variant="body2" lineHeight="24px">
                  {t(`attraction.onboard.primary_market.${market}`)}
                </Typography>
              </GradientBorderedBox>
            );
          })}
          <ErrorLabel error={formik.errors.market} />
        </CenteredColBox>
        <CenteredColBox
          gap={{ xs: spacing.ctaSpacingMobile, lg: spacing.ctaSpacingDesktop }}
          mt={{ xs: spacing.regularMobile, lg: spacing.regularDesktop }}
          fullWidth
        >
          <Button
            type="submit"
            fullWidth
            data-test="onboard.primaryMarket.submitButton"
          >
            {t("sign_in.continue")}
          </Button>
          <Link onClick={(e) => onPreviousStep(e)}>{t("onboard.back")}</Link>
        </CenteredColBox>
      </Box>
    </Form>
  );
};
